import axios from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import img2 from '../../assest/loading1.gif';
import { BaseUrl, getAuthHeaders } from '../../components/BaseURl/BaseUrl';
import HOC from '../../components/HOC/HOC';
import { SuccessfullyModal } from '../../components/Modals/Modals';
import addjob from '../../css/style.module.css';
import { getApi } from '../../Repository/Api';
import endPoints from '../../Repository/apiConfig';
import { formatDateTimeP } from '../../utils/utils';
import {
  approveStaffJob,
  canApproveStaffJob,
  getApprovalColor,
  getApprovalLabel,
} from './timesheetJobActions';
import TimesheetApproveModal from './TimesheetApproveModal';

const ViewTimesheet = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [partnerId, setPartnerId] = useState('');
  const [partnerName, setPartnerName] = useState('');
  const [partnerList, setPartnerList] = useState([]);
  const [toTime, setToTime] = useState('');
  const [fromTime, setFromTime] = useState('');
  const [status, setStatus] = useState('');
  const [timesheet, setTimesheet] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [locationLogsData, setLocationLogsData] = useState(null);
  const [locationLogsLoading, setLocationLogsLoading] = useState(false);

  const fetchLocationLogs = useCallback(async (staffId, bookingId) => {
    if (!staffId || !bookingId) return;

    setLocationLogsLoading(true);
    try {
      const response = await axios.get(
        `${BaseUrl}${endPoints.getStaffLocationLogs(staffId, bookingId)}`,
        getAuthHeaders()
      );
      setLocationLogsData(response.data?.data || null);
    } catch {
      setLocationLogsData(null);
    } finally {
      setLocationLogsLoading(false);
    }
  }, []);

  const fetchData = useCallback(async () => {
    await getApi(endPoints.getsTimesheetbyid(id), {
      setResponse: (response) => {
        const staffId = response?.data?.staff?._id || '';
        const bookingId = response?.data?.bookingId || '';

        setTimesheet(response?.data || null);
        setPartnerId(staffId);
        setPartnerName(response?.data?.staff?.firstName || '');
        setToTime(response?.data?.fromTime?.slice(0, 16) || '');
        setFromTime(response?.data?.toTime?.slice(0, 16) || '');
        setStatus(response?.data?.jobStatus || '');

        fetchLocationLogs(staffId, bookingId);
      },
      setLoading,
      errorMsg: 'Failed to fetch data!',
    });
  }, [id, fetchLocationLogs]);

  const fetchPartner = async () => {
    await getApi(endPoints.getallstaff, {
      setResponse: setPartnerList,
      setLoading: setLoading,
      errorMsg: 'Failed to fetch partners!',
    });
  };

  useEffect(() => {
    fetchPartner();
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApproveConfirm = async () => {
    setActionLoading(true);
    const ok = await approveStaffJob(id, { onSuccess: fetchData });
    setActionLoading(false);

    if (ok) {
      setShowApproveModal(false);
    }
  };

  const formatDateTime = (datetime) => {
    if (!datetime) return '';
    const date = new Date(datetime);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const outsideLocationRows = useMemo(() => {
    if (!locationLogsData?.logs?.length) return [];

    return locationLogsData.logs.flatMap((log) =>
      (log.locations || [])
        .filter((loc) => loc.outsideDesignatedArea)
        .map((loc, index) => ({
          key: `${log._id}-${loc.index ?? index}`,
          attemptAt: log.createdAt,
          recordedAt: loc.recordedAt,
          latitude: loc.coordinates?.[0],
          longitude: loc.coordinates?.[1],
          distanceM: loc.distanceM,
          maxRadiusMeters: loc.maxRadiusMeters ?? locationLogsData.maxRadiusMeters,
        }))
    );
  }, [locationLogsData]);

  const clientLocation = locationLogsData?.clientLocation;

  const openMap = (jobLat, jobLng, staffLat, staffLng) => {
    const url = `https://www.google.com/maps/dir/${jobLat},${jobLng}/${staffLat},${staffLng}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <TimesheetApproveModal
        show={showApproveModal}
        onHide={() => !actionLoading && setShowApproveModal(false)}
        onConfirm={handleApproveConfirm}
        approving={actionLoading}
        staffName={
          timesheet?.staff
            ? `${timesheet.staff.firstName || ''} ${timesheet.staff.lastName || ''}`.trim()
            : ''
        }
        customerName={
          timesheet?.user
            ? `${timesheet.user.firstName || ''} ${timesheet.user.lastName || ''}`.trim()
            : ''
        }
      />
      <SuccessfullyModal
        show={show}
        handleClose={() => setShow(false)}
        text={'Successfully Updated'}
      />
      <div className={addjob.jobmanagementCotainer}>
        <div className={addjob.jobtoppart}>
          <div className={addjob.pagetitle}>
            <h6>View Timesheet</h6>
          </div>
          <div className={addjob.rightbuttons}>
            <div className={addjob.rightbuttons2} onClick={() => navigate(-1)}>
              <p>View Timesheets</p>
            </div>
          </div>
        </div>

        <div className={addjob.addjobcontainer}>
          <div className={addjob.addjobsubcontainer}>
            <div className={addjob.addjobtopic}>
              <h6>Timesheet Details</h6>
            </div>
            {loading ? (
              <div className={addjob.loadingnormal}>
                <img src={img2} alt="Loading" width={100} height={100} />
              </div>
            ) : (
              <>
                <div className={addjob.addjobinputs}>
                  <label htmlFor="">Select Employee*</label>
                  <select
                    value={partnerId}
                    onChange={(e) => {
                      const selectedPartner = partnerList?.data?.find(
                        (cat) => cat._id === e.target.value
                      );
                      setPartnerId(e.target.value);
                      setPartnerName(selectedPartner?.name || '');
                    }}
                  >
                    <option value="">Select Partner</option>
                    {partnerList?.data?.map((partner) => (
                      <option key={partner._id} value={partner._id}>
                        {partner.firstName} {partner.lastName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={addjob.addnewbanner}>
                  <div className={addjob.addjobtopic}>
                    <h6>Timings</h6>
                  </div>
                  <div className={addjob.addvouchercontainer}>
                    <div className={addjob.addjobinputs}>
                      <label htmlFor="">Start Date *</label>
                      <input
                        type="datetime-local"
                        value={formatDateTime(toTime)}
                        onChange={(e) => setToTime(e.target.value)}
                      />
                    </div>
                    <div className={addjob.addjobinputs}>
                      <label htmlFor="">End Date*</label>
                      <input
                        type="datetime-local"
                        value={formatDateTime(fromTime)}
                        onChange={(e) => setFromTime(e.target.value)}
                      />
                    </div>
                    <div className={addjob.addjobinputs}>
                      <label htmlFor="">Status*</label>
                      <select onChange={(e) => setStatus(e.target.value)} value={status} disabled>
                        <option value="">Select</option>
                        <option value="Completed">Completed</option>
                        <option value="Pending">Pending</option>
                        <option value="Started">Started</option>
                        <option value="Upcoming">Upcoming</option>
                      </select>
                    </div>
                    <div className={addjob.addjobinputs}>
                      <label htmlFor="">Customer Approval</label>
                      <input
                        type="text"
                        value={getApprovalLabel(timesheet)}
                        readOnly
                        style={{
                          color: getApprovalColor(timesheet),
                          fontWeight: 600,
                        }}
                      />
                    </div>
                    <div className={addjob.addjobinputs}>
                      <label htmlFor="">Approval Required (shift)</label>
                      <input
                        type="text"
                        value={
                          timesheet?.approvalRequired === true
                            ? 'Yes'
                            : timesheet?.approvalRequired === false
                              ? 'No'
                              : '—'
                        }
                        readOnly
                      />
                    </div>
                  </div>
                </div>

                <div className={addjob.addnewbanner} style={{ marginTop: '24px' }}>
                  <div className={addjob.addjobtopic}>
                    <h6>Location history (outside job area)</h6>
                  </div>

                  {locationLogsLoading ? (
                    <div className={addjob.loadingnormal}>
                      <img src={img2} alt="Loading location logs" width={60} height={60} />
                    </div>
                  ) : (
                    <>
                      {clientLocation?.latitude != null && clientLocation?.longitude != null && (
                        <p style={{ marginBottom: '12px', color: '#5A5A5A' }}>
                          Job site: {clientLocation.latitude}, {clientLocation.longitude}
                          {locationLogsData?.maxRadiusMeters != null &&
                            ` · Allowed radius: ${locationLogsData.maxRadiusMeters} m`}
                        </p>
                      )}

                      {outsideLocationRows.length === 0 ? (
                        <p style={{ color: '#5A5A5A' }}>
                          No location samples recorded outside the designated job area for this
                          booking.
                        </p>
                      ) : (
                        <div className={addjob.tablecontainer}>
                          <table className={addjob.table1}>
                            <thead>
                              <tr>
                                <th>Recorded at</th>
                                <th>Latitude</th>
                                <th>Longitude</th>
                                <th>Distance from Job Location</th>
                                <th>Max radius (m)</th>
                                <th>Log attempt</th>
                                <th>View location</th>
                              </tr>
                            </thead>
                            <tbody>
                              {outsideLocationRows.map((row) => (
                                <tr key={row.key}>
                                  <td>{formatDateTimeP(row.recordedAt)}</td>
                                  <td>{row.latitude ?? '—'}</td>
                                  <td>{row.longitude ?? '—'}</td>
                                  <td>{row.distanceM ?? '—'}</td>
                                  <td>{row.maxRadiusMeters ?? '—'}</td>
                                  <td>{formatDateTimeP(row.attemptAt)}</td>
                                  <td>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        openMap(
                                          clientLocation?.latitude,
                                          clientLocation?.longitude,
                                          row.latitude,
                                          row.longitude
                                        )
                                      }
                                      disabled={
                                        clientLocation?.latitude == null ||
                                        clientLocation?.longitude == null ||
                                        row.latitude == null ||
                                        row.longitude == null
                                      }
                                      style={{ padding: '6px 10px' }}
                                    >
                                      View
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
          <div className={addjob.addjobbutton}>
            <div className={addjob.rightbuttons}>
              <button onClick={() => navigate('/timesheet-Management')}>Back</button>
            </div>
            {canApproveStaffJob(timesheet) && (
              <div className={addjob.rightbuttons}>
                <button
                  type="button"
                  onClick={() => setShowApproveModal(true)}
                  disabled={actionLoading}
                  style={{ background: '#1E88E5' }}
                >
                  Approve Shift
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default HOC(ViewTimesheet);
